import { cn } from "@/lib/cn";

type Tone =
  | "logo"
  | "brass"
  | "brand"
  | "ok"
  | "warn"
  | "danger"
  | "info"
  | "neutral";

const TONE_VAR: Record<Exclude<Tone, "logo">, string> = {
  brass: "var(--color-brass)",
  brand: "var(--color-brand)",
  ok: "var(--color-ok)",
  warn: "var(--color-warn)",
  danger: "var(--color-danger)",
  info: "var(--color-info)",
  neutral: "var(--color-ink-faint)",
};

const LOGO_TONE_COLORS = [
  "var(--color-logo-magenta)",
  "var(--color-logo-lime)",
  "var(--color-logo-cyan)",
] as const;

export interface ButtonRingProps {
  /** 0..1 — how much of the rim is "stitched". Omit for a static mark. */
  progress?: number;
  size?: number;
  tone?: Tone;
  /** Button holes in the center: 2 (shirt) or 4 (jacket). */
  holes?: 2 | 4;
  /** Spin the stitch — used for the loading indicator. */
  spinning?: boolean;
  className?: string;
  title?: string;
}

/**
 * The signature mark. A garment button rendered as concentric geometry:
 * an outer rim, a "stitch" arc that fills with progress (payment / document
 * status), and 2 or 4 holes. Used as the app logo and as the status indicator
 * throughout — a button being sewn on.
 */
export function ButtonRing({
  progress,
  size = 20,
  tone = "brass",
  holes = 4,
  spinning = false,
  className,
  title,
}: ButtonRingProps) {
  const r = 40;
  const circ = 2 * Math.PI * r;
  const clamped =
    progress === undefined ? 1 : Math.max(0, Math.min(1, progress));
  const color = tone === "logo" ? LOGO_TONE_COLORS[0] : TONE_VAR[tone];
  const logoArcSegmentLength = (circ * clamped) / LOGO_TONE_COLORS.length;
  const logoArcVisibleLength = Math.max(0.01, logoArcSegmentLength - 2.5);
  const faceRadius = 26;
  const faceCirc = 2 * Math.PI * faceRadius;
  const logoFaceSegmentLength = faceCirc / LOGO_TONE_COLORS.length;
  const logoFaceVisibleLength = logoFaceSegmentLength - 1.75;

  const holePositions =
    holes === 2
      ? [
          [50, 36],
          [50, 64],
        ]
      : [
          [38, 38],
          [62, 38],
          [38, 62],
          [62, 62],
        ];

  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={cn("shrink-0", spinning && "motion-safe:animate-spin", className)}
      role="img"
      aria-label={title}
      style={{ overflow: "visible" }}
    >
      {title ? <title>{title}</title> : null}
      {/* rim track */}
      <circle
        className="button-ring__track"
        cx="50"
        cy="50"
        r={r}
        fill="none"
        stroke="var(--color-line-strong)"
        strokeWidth="8"
      />
      {/* stitch arc — starts at top, fills clockwise with progress */}
      {clamped > 0 && tone === "logo" ? (
        LOGO_TONE_COLORS.map((segmentColor, index) => (
          <circle
            key={`arc-${index}`}
            className="button-ring__arc"
            cx="50"
            cy="50"
            r={r}
            fill="none"
            stroke={segmentColor}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={`${logoArcVisibleLength} ${circ - logoArcVisibleLength}`}
            strokeDashoffset={-logoArcSegmentLength * index}
            transform="rotate(-90 50 50)"
            style={{ transition: "stroke-dasharray .5s ease" }}
          />
        ))
      ) : clamped > 0 ? (
        <circle
          className="button-ring__arc"
          cx="50"
          cy="50"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={`${circ * clamped} ${circ}`}
          transform="rotate(-90 50 50)"
          style={{ transition: "stroke-dasharray .5s ease" }}
        />
      ) : null}
      {/* inner button face line */}
      {tone === "logo" ? (
        LOGO_TONE_COLORS.map((segmentColor, index) => (
          <circle
            key={`face-${index}`}
            className="button-ring__face"
            cx="50"
            cy="50"
            r={faceRadius}
            fill="none"
            stroke={segmentColor}
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={`${logoFaceVisibleLength} ${faceCirc - logoFaceVisibleLength}`}
            strokeDashoffset={-logoFaceSegmentLength * index}
            transform="rotate(-90 50 50)"
            opacity="0.55"
          />
        ))
      ) : (
        <circle
          className="button-ring__face"
          cx="50"
          cy="50"
          r={faceRadius}
          fill="none"
          stroke={color}
          strokeWidth="3"
          opacity="0.55"
        />
      )}
      {/* holes */}
      {holePositions.map(([cx, cy], i) => (
        <circle
          key={i}
          className="button-ring__hole"
          cx={cx}
          cy={cy}
          r="5.5"
          fill={
            tone === "logo"
              ? LOGO_TONE_COLORS[i % LOGO_TONE_COLORS.length]
              : color
          }
        />
      ))}
    </svg>
  );
}
